import * as React from 'react';
import { ReduxState } from 'ducks';
import { useSelector } from 'react-redux';
import { getImageProps } from 'next/image';

import { getEditorJsResizedUrl } from 'lib/util/imageUrl';
import { useIsSmallScreen } from './useIsSmallScreen';
import { getPreloadTargets } from './util';

const PIN_DETAILS_FIRST_IMAGES_DELAY = 3000;
const PIN_DETAILS_SECONDARY_IMAGES_DELAY = 7000;
const PIN_DETAILS_EDITOR_JS_IMAGES_DELAY = 8000;
const PIN_DETAILS_GENERAL_IMAGES_DELAY = 2000;

const getSmallScreenEditorJsImageSrcSet = (url: string) => {
  return [640 /* 1x */, 750 /* 2x */, 1080 /* 3x */]
    .map((width, idx) => {
      const resizedUrl = getEditorJsResizedUrl(url, {
        resize: {
          width,
          fit: 'cover',
        },
      });

      return `${resizedUrl} ${idx + 1}x`;
    })
    .join(', ');
};

const getLargeScreenEditorJsImageSrcSet = (url: string) => {
  return [1080 /* 1x */, 1080 /* 2x */, 1080 /* 3x */]
    .map((width, idx) => {
      const resizedUrl = getEditorJsResizedUrl(url, {
        resize: {
          width,
          fit: 'cover',
        },
      });

      return `${resizedUrl} ${idx + 1}x`;
    })
    .join(', ');
};

export const MediaPreloader = ({ isStandalone }: { isStandalone: boolean }) => {
  const [shouldPreloadImages, setShouldPreloadImages] = React.useState(false);
  const [preloadPinDetailsFirstImages, setPreloadPinDetailsFirstImages] = React.useState(false);
  const [preloadPinDetailsSecondaryImages, setPreloadPinDetailsSecondaryImages] = React.useState(
    false
  );
  const [preloadPinDetailsEditorJsImages, setPreloadPinDetailsEditorJsImages] = React.useState(
    false
  );

  const digitalMap = useSelector((state: ReduxState) => state.universal.digitalMap.map);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  const isSmallScreen = useIsSmallScreen();

  React.useEffect(() => {
    setTimeout(() => {
      setPreloadPinDetailsFirstImages(true);
    }, PIN_DETAILS_FIRST_IMAGES_DELAY);
    setTimeout(() => {
      setPreloadPinDetailsSecondaryImages(true);
    }, PIN_DETAILS_SECONDARY_IMAGES_DELAY);
    setTimeout(() => {
      setPreloadPinDetailsEditorJsImages(true);
    }, PIN_DETAILS_EDITOR_JS_IMAGES_DELAY);
    setTimeout(() => {
      setShouldPreloadImages(true);
    }, PIN_DETAILS_GENERAL_IMAGES_DELAY);
  }, []);

  const preloadTargets = React.useMemo(() => getPreloadTargets(digitalMap, settings), [
    digitalMap,
    settings,
  ]);

  React.useEffect(() => {
    if (preloadPinDetailsEditorJsImages && preloadTargets.editorJsImageUrls?.length) {
      preloadTargets.editorJsImageUrls.forEach((url: string) => {
        const img = new window.Image();
        img.srcset = isSmallScreen
          ? getSmallScreenEditorJsImageSrcSet(url)
          : getLargeScreenEditorJsImageSrcSet(url);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preloadPinDetailsEditorJsImages,
    preloadTargets.editorJsImageUrls?.length,
    isSmallScreen,
    isStandalone,
  ]);

  React.useEffect(() => {
    if (
      digitalMap?.pwa_settings?.enable_pwa_installation &&
      shouldPreloadImages &&
      preloadTargets.overlayImages?.length
    ) {
      preloadTargets.overlayImages.forEach((url: string) => {
        const img = new window.Image();
        img.src = url;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    digitalMap?.pwa_settings?.enable_pwa_installation,
    preloadTargets.overlayImages?.length,
    shouldPreloadImages,
    isStandalone,
  ]);

  React.useEffect(() => {
    if (
      digitalMap?.pwa_settings?.enable_pwa_installation &&
      shouldPreloadImages &&
      preloadTargets.logoImageUrl
    ) {
      const img = new window.Image();
      img.src = preloadTargets.logoImageUrl;
    }
  }, [
    digitalMap?.pwa_settings?.enable_pwa_installation,
    shouldPreloadImages,
    preloadTargets.logoImageUrl,
    isStandalone,
  ]);

  React.useEffect(() => {
    if (shouldPreloadImages && preloadTargets.pinThumbnails?.length) {
      preloadTargets.pinThumbnails.forEach((url: string) => {
        const { props } = getImageProps({
          src: url,
          alt: '',
          width: 94,
          height: 94,
        });
        const img = new window.Image(94, 94);
        if (props.srcSet) img.srcset = props.srcSet;
        if (props.src) img.src = props.src;
        if (props.sizes) img.sizes = props.sizes;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPreloadImages, preloadTargets.pinThumbnails?.length, isStandalone]);

  // Preload categoryIcons using getImageProps and Image()
  React.useEffect(() => {
    if (shouldPreloadImages) {
      preloadTargets.categoryIcons?.forEach((url: string) => {
        const { props } = getImageProps({
          src: url,
          alt: '',
          width: 12,
          height: 12,
        });
        const img = new window.Image(12, 12);
        if (props.srcSet) img.srcset = props.srcSet;
        if (props.src) img.src = props.src;
        if (props.sizes) img.sizes = props.sizes;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPreloadImages, preloadTargets.categoryIcons?.length, isStandalone]);

  // Preload menuIcons using getImageProps and Image()
  React.useEffect(() => {
    if (shouldPreloadImages) {
      preloadTargets.menuIcons?.forEach((url: string) => {
        const { props } = getImageProps({
          src: url,
          alt: '',
          width: 16,
          height: 24,
          unoptimized: true,
        });
        const img = new window.Image(16, 24);
        if (props.srcSet) img.srcset = props.srcSet;
        if (props.src) img.src = props.src;
        if (props.sizes) img.sizes = props.sizes;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPreloadImages, preloadTargets.menuIcons?.length, isStandalone]);

  // Preload pinDetailsFirstImages using getImageProps and Image()
  React.useEffect(() => {
    if (preloadPinDetailsFirstImages && preloadTargets.pinDetailsFirstImages?.length) {
      preloadTargets.pinDetailsFirstImages.forEach((url: string) => {
        const { props } = getImageProps({
          src: url,
          alt: '',
          width: 328,
          height: 250,
        });
        const img = new window.Image(328, 250);
        if (props.srcSet) img.srcset = props.srcSet;
        if (props.src) img.src = props.src;
        if (props.sizes) img.sizes = props.sizes;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadPinDetailsFirstImages, preloadTargets.pinDetailsFirstImages?.length, isStandalone]);

  // Preload pinDetailsSecondaryImages using getImageProps and Image()
  React.useEffect(() => {
    if (preloadPinDetailsSecondaryImages && preloadTargets.pinDetailsSecondaryImages?.length) {
      preloadTargets.pinDetailsSecondaryImages.forEach((url: string) => {
        const { props } = getImageProps({
          src: url,
          alt: '',
          width: 328,
          height: 250,
        });
        const img = new window.Image(328, 250);
        if (props.srcSet) img.srcset = props.srcSet;
        if (props.src) img.src = props.src;
        if (props.sizes) img.sizes = props.sizes;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preloadPinDetailsSecondaryImages,
    preloadTargets.pinDetailsSecondaryImages?.length,
    isStandalone,
  ]);

  // Preload editorJsVideoUrls using Video() in useEffect
  React.useEffect(() => {
    if (preloadPinDetailsEditorJsImages && preloadTargets.editorJsVideoUrls?.length) {
      preloadTargets.editorJsVideoUrls.forEach((url: string) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = url;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadPinDetailsEditorJsImages, preloadTargets.editorJsVideoUrls?.length, isStandalone]);

  // Preload editorJsAudioUrls using Audio() in useEffect
  React.useEffect(() => {
    if (preloadPinDetailsEditorJsImages && preloadTargets.editorJsAudioUrls?.length) {
      preloadTargets.editorJsAudioUrls.forEach((url: string) => {
        const audio = new window.Audio();
        audio.preload = 'metadata';
        audio.src = url;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadPinDetailsEditorJsImages, preloadTargets.editorJsAudioUrls?.length, isStandalone]);

  return null;
};
